import React from 'react';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { FileText, Shield, AlertCircle, Scale } from 'lucide-react';

export default function Terms() {
  const { language, isRTL } = useLanguage();

  const sections = language === 'he' ? [
    {
      icon: FileText,
      title: 'תנאים כלליים',
      content: `ברוכים הבאים ל-AI PhotoBooth. על ידי שימוש בשירותים שלנו, אתם מסכימים לתנאי שימוש אלה. אנא קראו אותם בעיון.

השירות שלנו מאפשר לכם ליצור תמונות מעובדות באמצעות טכנולוגיית בינה מלאכותית. התמונות שאתם יוצרים הן לשימוש אישי בלבד, אלא אם רכשתם רישיון מסחרי.`
    },
    {
      icon: Shield,
      title: 'שימוש מותר',
      content: `אתם מתחייבים להשתמש בשירות רק למטרות חוקיות. אסור להעלות תמונות של אנשים אחרים ללא הסכמתם. אסור ליצור תוכן פוגעני, מטריד, או בלתי חוקי.

אנו שומרים לעצמנו את הזכות לסרב לשירות או לחסום משתמשים שמפרים תנאים אלה.`
    },
    {
      icon: Scale,
      title: 'קניין רוחני',
      content: `הטכנולוגיה, העיצוב, והתוכן של האתר הם קניין רוחני של AI PhotoBooth. התמונות שאתם יוצרים באמצעות השירות הן בבעלותכם, בכפוף לתנאי הרישיון.

אתם מעניקים לנו רישיון לשמור ולעבד את התמונות שלכם לצורך מתן השירות.`
    },
    {
      icon: AlertCircle,
      title: 'הגבלת אחריות',
      content: `השירות מסופק "כפי שהוא" (AS IS). איננו מתחייבים לזמינות רציפה או לאיכות מסוימת של התוצאות.

לא נהיה אחראים לכל נזק ישיר או עקיף הנובע משימוש בשירות. סך האחריות שלנו מוגבל לסכום ששילמתם עבור השירות.`
    }
  ] : [
    {
      icon: FileText,
      title: 'General Terms',
      content: `Welcome to AI PhotoBooth. By using our services, you agree to these terms of use. Please read them carefully.

Our service allows you to create AI-processed images. The images you create are for personal use only, unless you have purchased a commercial license.`
    },
    {
      icon: Shield,
      title: 'Acceptable Use',
      content: `You agree to use the service only for lawful purposes. You may not upload images of other people without their consent. You may not create offensive, harassing, or illegal content.

We reserve the right to refuse service or block users who violate these terms.`
    },
    {
      icon: Scale,
      title: 'Intellectual Property',
      content: `The technology, design, and content of the website are intellectual property of AI PhotoBooth. The images you create using the service are yours, subject to license terms.

You grant us a license to store and process your images for the purpose of providing the service.`
    },
    {
      icon: AlertCircle,
      title: 'Limitation of Liability',
      content: `The service is provided "AS IS". We do not guarantee continuous availability or specific quality of results.

We will not be liable for any direct or indirect damages resulting from use of the service. Our total liability is limited to the amount you paid for the service.`
    }
  ];

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'he' ? 'תנאי שימוש' : 'Terms of Service'}
          </h1>
          <p className="text-slate-400">
            {language === 'he' ? 'עדכון אחרון: ינואר 2024' : 'Last updated: January 2024'}
          </p>
        </motion.div>

        <div className="space-y-8">
          {sections.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-violet-600/20 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
              </div>
              <div className="text-slate-400 leading-relaxed whitespace-pre-line">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
